// SME Categories and Subcategories mapping
// ─────────────────────────────────────────────────────────────────────────────
// SME Taxonomy  (3 levels)
//   type        → broad knowledge domain   (e.g. "Science")
//   smeCategory → sub-domain               (e.g. "Physics")
//   subCategory → fine specialisation      (e.g. "QuantumPhysics")
// ─────────────────────────────────────────────────────────────────────────────

// Level 1 – Broad domains (matches backend SME_TYPE enum)
export const SME_TYPES = [
  'Science',
  'Business',
  'Technology',
  'Healthcare',
  'Politics',
  'Law',
  'Education',
  'Media',
  'Engineering',
  'ArtsAndHumanities',
  'Environment',
  'SportsAndWellness',
];

export const TYPE_DISPLAY_NAMES = {
  'Science':           'Science',
  'Business':          'Business',
  'Technology':        'Technology',
  'Healthcare':        'Healthcare',
  'Politics':          'Politics',
  'Law':               'Law',
  'Education':         'Education',
  'Media':             'Media',
  'Engineering':       'Engineering',
  'ArtsAndHumanities': 'Arts & Humanities',
  'Environment':       'Environment',
  'SportsAndWellness': 'Sports & Wellness',
};

// Level 2 – Categories keyed by type
export const CATEGORIES_BY_TYPE = {
  'Science': [
    'Physics', 'Chemistry', 'Biology', 'Mathematics',
    'Psychology', 'Sociology', 'Neuroscience', 'Astronomy',
    'Geology', 'Anthropology',
  ],
  'Business': [
    'Economics', 'Finance', 'Marketing', 'Management',
    'Entrepreneurship', 'Accounting', 'HumanResources',
    'SupplyChain', 'RealEstate', 'ConsumerBehavior',
  ],
  'Technology': [
    'SoftwareDevelopment', 'ArtificialIntelligence', 'Cybersecurity',
    'CloudComputing', 'DataEngineering', 'Blockchain',
    'Hardware', 'IoT', 'Robotics', 'QuantumComputing',
  ],
  'Healthcare': [
    'ClinicalMedicine', 'PublicHealth', 'Pharmacy',
    'MentalHealth', 'Nutrition', 'Nursing',
    'Genetics', 'Oncology', 'Epidemiology',
  ],
  'Politics': [
    'DomesticPolicy', 'ForeignPolicy', 'InternationalRelations',
    'Governance', 'ElectoralPolitics', 'PublicAdministration',
    'DefenseAndSecurity', 'HumanRights',
  ],
  'Law': [
    'CriminalLaw', 'CorporateLaw', 'ConstitutionalLaw',
    'IntellectualProperty', 'EnvironmentalLaw', 'FamilyLaw',
    'LaborLaw', 'TaxLaw', 'Compliance',
  ],
  'Education': [
    'HigherEducation', 'K12Education', 'EarlyChildhood',
    'SpecialEducation', 'CurriculumDesign', 'EdTech',
    'VocationalTraining',
  ],
  'Media': [
    'Journalism', 'Broadcasting', 'DigitalMedia',
    'PublicRelations', 'ContentStrategy', 'FilmAndTV', 'SocialMedia',
  ],
  'Engineering': [
    'CivilEngineering', 'MechanicalEngineering', 'ElectricalEngineering',
    'ChemicalEngineering', 'AerospaceEngineering',
    'BiomedicalEngineering', 'NuclearEngineering',
  ],
  'ArtsAndHumanities': [
    'History', 'Philosophy', 'Literature', 'FineArts',
    'Music', 'Linguistics', 'CulturalStudies', 'Archaeology', 'Theatre',
  ],
  'Environment': [
    'ClimateScience', 'Conservation', 'RenewableEnergy',
    'EnvironmentalPolicy', 'OceanScience', 'Forestry', 'UrbanSustainability',
  ],
  'SportsAndWellness': [
    'SportsScience', 'Athletics', 'Coaching',
    'SportsMedicine', 'Fitness', 'MentalWellness',
  ],
};

// Level 3 – SubCategories keyed by category
export const SUBCATEGORIES_BY_CATEGORY = {
  // Science
  'Physics':       ['QuantumPhysics','Astrophysics','ParticlePhysics','CondensedMatter','Thermodynamics','Optics','NuclearPhysics'],
  'Chemistry':     ['OrganicChemistry','InorganicChemistry','PhysicalChemistry','AnalyticalChemistry','Biochemistry','MaterialsChemistry'],
  'Biology':       ['Genetics','Ecology','Microbiology','CellBiology','EvolutionaryBiology','MarineBiology','Zoology','Botany'],
  'Mathematics':   ['Algebra','Calculus','Statistics','Probability','NumberTheory','Topology','AppliedMathematics'],
  'Psychology':    ['ClinicalPsychology','CognitivePsychology','BehavioralPsychology','NeuroPsychology','SocialPsychology'],
  'Sociology':     ['UrbanSociology','CulturalSociology','PoliticalSociology','MedicalSociology','EnvironmentalSociology'],
  'Neuroscience':  ['CognitiveNeuroscience','BehavioralNeuroscience','ComputationalNeuroscience','ClinicalNeuroscience'],
  'Astronomy':     ['Cosmology','PlanetaryScience','StellarAstronomy','GalacticAstronomy'],
  'Geology':       ['Geomorphology','Volcanology','Seismology','Mineralogy','Paleontology'],
  'Anthropology':  ['CulturalAnthropology','PhysicalAnthropology','LinguisticAnthropology','Bioarchaeology'],

  // Business
  'Economics':         ['Macroeconomics','Microeconomics','BehavioralEconomics','DevelopmentEconomics','TradeEconomics','LaborEconomics'],
  'Finance':           ['CorporateFinance','InvestmentBanking','PersonalFinance','RiskManagement','QuantitativeFinance','PrivateEquity'],
  'Marketing':         ['DigitalMarketing','ContentMarketing','BrandMarketing','MarketResearch','PerformanceMarketing','SEO'],
  'Management':        ['StrategicManagement','ProjectManagement','OperationsManagement','ChangeManagement'],
  'Entrepreneurship':  ['StartupFounder','ProductManagement','VentureCapital','BusinessDevelopment','SocialEntrepreneurship'],
  'Accounting':        ['FinancialAccounting','ManagementAccounting','AuditingAndAssurance','TaxAccounting','ForensicAccounting'],
  'HumanResources':    ['TalentAcquisition','LearningAndDevelopment','CompensationAndBenefits','EmployeeRelations'],
  'SupplyChain':       ['Logistics','Procurement','InventoryManagement','SupplyChainAnalytics'],
  'RealEstate':        ['CommercialRealEstate','ResidentialRealEstate','PropertyManagement','RealEstateInvesting'],
  'ConsumerBehavior':  ['PurchasingBehavior','ConsumerPsychology','CustomerExperience','BrandLoyalty'],

  // Technology
  'SoftwareDevelopment': ['BackendDevelopment','FrontendDevelopment','MobileDevelopment','DevOps','SoftwareArchitecture','SystemDesign'],
  'ArtificialIntelligence': ['MachineLearning','DeepLearning','NLP','ComputerVision','ReinforcementLearning','AIEthics'],
  'Cybersecurity':     ['NetworkSecurity','ApplicationSecurity','PenetrationTesting','IncidentResponse','CloudSecurity'],
  'CloudComputing':    ['AWS','Azure','GoogleCloud','CloudArchitecture','Serverless','Microservices'],
  'DataEngineering':   ['DataPipelines','DataWarehousing','BigData','StreamProcessing','DataModeling'],
  'Blockchain':        ['SmartContracts','DeFi','NFTs','CryptocurrencyTrading','BlockchainDevelopment'],
  'Hardware':          ['MicroelectronicsDesign','EmbeddedSystems','FPGA','SemiconductorPhysics'],
  'IoT':               ['SensorNetworks','SmartHome','IndustrialIoT','WearableTech'],
  'Robotics':          ['RoboticsEngineering','AutonomousSystems','RobotVision','HumanRobotInteraction'],
  'QuantumComputing':  ['QuantumAlgorithms','QuantumHardware','QuantumCryptography'],

  // Healthcare
  'ClinicalMedicine':  ['Cardiology','Neurology','Oncology','PediatricMedicine','EmergencyMedicine','InternalMedicine'],
  'PublicHealth':      ['Epidemiology','GlobalHealth','HealthPolicy','CommunityHealth','EnvironmentalHealth'],
  'Pharmacy':          ['ClinicalPharmacy','PharmaceuticalChemistry','Pharmacokinetics','DrugDiscovery'],
  'MentalHealth':      ['ClinicalPsychiatry','CognitiveBehavioralTherapy','TraumaTherapy','SubstanceAbuse'],
  'Nutrition':         ['ClinicalNutrition','SportsNutrition','DietaryEpidemiology','FoodScience'],
  'Nursing':           ['PediatricNursing','CriticalCareNursing','MidwiferyCare','PublicHealthNursing'],
  'Genetics':          ['MolecularGenetics','GenomicsAndSequencing','GeneTherapy','Epigenetics'],
  'Oncology':          ['ClinicalOncology','PediatricOncology','CancerResearch','RadiationTherapy'],
  'Epidemiology':      ['InfectiousDisease','ChronicDiseaseEpi','EnvironmentalEpi','MolecularEpi'],

  // Politics
  'DomesticPolicy':         ['EconomicPolicy','SocialPolicy','HealthcarePolicy','EducationPolicy','LaborPolicy'],
  'ForeignPolicy':          ['DiplomacyAndNegotiation','InternationalSecurity','SanctionsAndTrade','GeopoliticalAnalysis'],
  'InternationalRelations': ['GlobalGovernance','ConflictResolution','HumanRightsAdvocacy','RegionalStudies'],
  'Governance':             ['LocalGovernment','FederalGovernment','RegulationAndCompliance','PublicBudgeting'],
  'ElectoralPolitics':      ['CampaignStrategy','PoliticalCommunication','ElectionAnalysis','VoterBehavior'],
  'PublicAdministration':   ['PolicyImplementation','BureaucraticManagement','PublicServiceDelivery'],
  'DefenseAndSecurity':     ['NationalSecurity','MilitaryStrategy','CounterTerrorism','IntelligenceAnalysis'],
  'HumanRights':            ['CivilRights','ChildRights','WomensRights','RefugeeAndMigrantRights'],

  // Law
  'CriminalLaw':         ['WhiteCollarCrime','CriminalDefense','ProsecutionLaw','JuvenileJustice'],
  'CorporateLaw':        ['MergersAndAcquisitions','ContractLaw','CorporateGovernance','SecuritiesLaw'],
  'ConstitutionalLaw':   ['CivilLiberties','FederalismAndSeparation','SocialJusticeLaw','ElectionsAndVoting'],
  'IntellectualProperty':['PatentLaw','TrademarkAndCopyright','TradeSecrets','TechPatents'],
  'EnvironmentalLaw':    ['ClimateAndEnergyLaw','ResourceRegulation','LandUseAndZoning'],
  'FamilyLaw':           ['DivorceAndCustody','AdoptionLaw','DomesticViolence','ChildWelfare'],
  'LaborLaw':            ['CollectiveBargaining','WorkplaceRights','EmploymentDiscrimination','GigEconomy'],
  'TaxLaw':              ['CorporateTax','IndividualTax','InternationalTax','TaxLitigation'],
  'Compliance':          ['RegulatoryCompliance','AntiMoneyLaundering','DataPrivacyCompliance','HealthcareCompliance'],

  // Education
  'HigherEducation':    ['UniversityLeadership','ResearchInstitutions','AcademicPublishing','FacultyDevelopment'],
  'K12Education':       ['CurriculumAndPedagogy','ClassroomManagement','StudentAssessment','SpecialNeeds'],
  'EarlyChildhood':     ['ChildDevelopment','EarlyInterventions','MontessoriAndAlternative'],
  'SpecialEducation':   ['LearningDisabilities','InclusionPractices','BehavioralInterventions'],
  'CurriculumDesign':   ['LearningObjectives','InstructionalDesign','CompetencyBasedCurriculum'],
  'EdTech':             ['LearningManagementSystems','GamificationInLearning','AIInEducation','RemoteLearning'],
  'VocationalTraining': ['TradeSkills','ApprenticeshipPrograms','WorkforceDevelopment'],

  // Media
  'Journalism':       ['InvestigativeJournalism','DataJournalism','PoliticalJournalism','ScienceJournalism'],
  'Broadcasting':     ['RadioBroadcasting','NewsTV','Podcasting','StreamingMedia'],
  'DigitalMedia':     ['SocialMediaStrategy','ContentCreation','SEOAndGrowth','EmailMarketing'],
  'PublicRelations':  ['CrisisManagement','CorporateCommunications','MediaRelations','EventManagement'],
  'ContentStrategy':  ['ContentMarketing','EditorialStrategy','AudienceResearch','TransmediaStorytelling'],
  'FilmAndTV':        ['FilmDirection','Screenwriting','DocumentaryFilmmaking','FilmProduction'],
  'SocialMedia':      ['SocialMediaMarketing','CommunityManagement','InfluencerMarketing','SocialAnalytics'],

  // Engineering
  'CivilEngineering':        ['StructuralEngineering','TransportationEngineering','GeotechnicalEngineering','WaterResourcesEngineering'],
  'MechanicalEngineering':   ['ThermalEngineering','FluidMechanics','ManufacturingAndDesign','HVAC'],
  'ElectricalEngineering':   ['PowerEngineering','ControlSystems','ElectronicsDesign','Telecommunications'],
  'ChemicalEngineering':     ['ProcessEngineering','ReactionEngineering','MaterialsProcessing','Petrochemicals'],
  'AerospaceEngineering':    ['FlightDynamics','PropulsionSystems','Avionics','SpacecraftDesign'],
  'BiomedicalEngineering':   ['MedicalDeviceDesign','Biomechanics','BiomedicalImaging','TissueEngineering'],
  'NuclearEngineering':      ['ReactorDesignAndSafety','NuclearFuelCycle','RadiationProtection','FusionResearch'],

  // Arts & Humanities
  'History':          ['AncientHistory','MedievalHistory','ModernHistory','MilitaryHistory','SocialHistory'],
  'Philosophy':       ['EthicsAndMoralPhilosophy','PoliticalPhilosophy','PhilosophyOfScience','Logic'],
  'Literature':       ['PoetryAndFiction','LiteraryTheory','WorldLiterature','CreativeWriting'],
  'FineArts':         ['Painting','Sculpture','Printmaking','DigitalArt','ArtHistory'],
  'Music':            ['MusicComposition','MusicPerformance','MusicTheory','MusicProduction','Ethnomusicology'],
  'Linguistics':      ['Phonology','Morphology','Sociolinguistics','Psycholinguistics'],
  'CulturalStudies':  ['MediaCulture','GenderStudies','PostcolonialStudies','RaceAndEthnicity'],
  'Archaeology':      ['FieldArchaeology','ClassicalArchaeology','MarineArchaeology','Bioarchaeology'],
  'Theatre':          ['PerformanceStudies','Dramaturgy','TheatreDesign','DirectingAndProduction'],

  // Environment
  'ClimateScience':       ['ClimateModeling','CarbonCycleResearch','IceAndPermafrost','ExtremeWeather'],
  'Conservation':         ['WildlifeConservation','BiodiversityAndHabitat','MarineConservation','LandConservation'],
  'RenewableEnergy':      ['SolarEnergy','WindEnergy','HydropowerAndTidal','BioenergyAndBiofuels'],
  'EnvironmentalPolicy':  ['ClimatePolicy','PollutionRegulation','EnvironmentalJustice','CircularEconomy'],
  'OceanScience':         ['PhysicalOceanography','ChemicalOceanography','MarineEcology','CoastalManagement'],
  'Forestry':             ['Silviculture','ForestEcology','Agroforestry','FireEcology'],
  'UrbanSustainability':  ['SmartCities','GreenBuildingDesign','UrbanMobility','WasteAndWaterManagement'],

  // Sports & Wellness
  'SportsScience':   ['ExercisePhysiology','BiomechanicsInSports','StrengthAndConditioning','SportsNutritionScience'],
  'Athletics':       ['TrackAndField','TeamSports','IndividualSports','ParalympicAthletics'],
  'Coaching':        ['SportsCoaching','PerformanceAnalytics','YouthDevelopment','SportsLeadership'],
  'SportsMedicine':  ['InjuryPreventionAndRehab','SportsPsychology','OrthopedicSportsMedicine','ConcussionManagement'],
  'Fitness':         ['PersonalTraining','FitnessProgramming','BodybuildingAndStrength','GroupFitness'],
  'MentalWellness':  ['StressAndBurnout','MindfulnessAndMeditation','PositivePsychology','ResilienceTraining'],
};

// ─── Display name helpers ────────────────────────────────────────────────────
// Converts camelCase/PascalCase keys to readable labels, with explicit overrides.
const DISPLAY_OVERRIDES = {
  'ArtsAndHumanities': 'Arts & Humanities',
  'SportsAndWellness': 'Sports & Wellness',
  'K12Education': 'K-12 Education',
  'SEO': 'SEO',
  'HVAC': 'HVAC',
  'AWS': 'AWS',
  'Azure': 'Azure',
  'GoogleCloud': 'Google Cloud',
  'IoT': 'IoT',
  'NFTs': 'NFTs',
  'DeFi': 'DeFi',
  'FPGA': 'FPGA',
  'NLP': 'NLP',
  'AIEthics': 'AI Ethics',
  'AIInEducation': 'AI in Education',
  'HumanRobotInteraction': 'Human-Robot Interaction',
  'SEOAndGrowth': 'SEO & Growth',
  'EdTech': 'EdTech',
  'FilmAndTV': 'Film & TV',
  'MontessoriAndAlternative': 'Montessori & Alternative',
};

function toDisplayName(key) {
  if (DISPLAY_OVERRIDES[key]) return DISPLAY_OVERRIDES[key];
  // Insert space before each uppercase letter that follows a lowercase letter
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

export const getCategoriesForType = (type) => CATEGORIES_BY_TYPE[type] || [];
export const getSubcategoriesForCategory = (category) => SUBCATEGORIES_BY_CATEGORY[category] || [];
export const getCategoryDisplayName = (cat) => toDisplayName(cat);
export const getSubCategoryDisplayName = (sub) => toDisplayName(sub);
export const getTypeDisplayName = (type) => TYPE_DISPLAY_NAMES[type] || type;
